import {TARGET, startLessonTarget, startQuizTarget} from './targets';
import {SIMPLIFIED_FIRST_VISIT} from '../config/app.config';

/**
 * The guided first visit, in order (Sol realjourney Q1 A; owner 2026-10-09). Each step advances on exactly one
 * thing the learner does: a key press, a click on its target, a drag, or a moment in the world.
 *
 *   place    'quikbase' | 'world' - where the step happens; it is only shown there
 *   kind     'key'    "press ... on your keyboard" (no hand)
 *            'click'  the green hand and the pulsing green outline on `target`
 *            'drag'   hold the left mouse button and drag
 *            'panel'  the tour slide or the tour quiz
 *            'badge'  report the step to the server, which awards and pays its badge once
 *            'wait'   something the learner does in the world; `text` coaches it
 *   advanceOn  the event that completes the step (defaults: keys, click:<target>, drag, badge:reported)
 *   skipOn / skipTo  an event that jumps ahead to `skipTo` instead (the Upgrade steps, when there is no Upgrade button)
 *   needs / without  the world state the step needs ('store' | 'menu' | 'tutor' | 'lesson' | 'plans') and the step
 *            to move to when that state is gone, so a reload, a return from checkout or a closed panel never leaves
 *            an instruction the learner cannot follow (Sol tour-dead-ends B)
 *   praisedText  the text used instead when the Upgrade steps were skipped, so the praise they carried is not lost
 *   pays     a control the server pays 10 QuikCoins for, once, when the step is done (Sol onboarding-build Q2 A)
 *
 * Text may name the tutor with {tutor}. The lesson-menu targets name the tour's lesson with {lesson}.
 */
export const STEPS_V1 = [
  {id: 'walk_w', place: 'quikbase', kind: 'key', keys: ['KeyW', 'ArrowUp'], key: 'W', text: 'Press W on your keyboard to walk forward'},
  {id: 'walk_a', place: 'quikbase', kind: 'key', keys: ['KeyA', 'ArrowLeft'], key: 'A', text: 'Press A on your keyboard to walk left'},
  {id: 'walk_s', place: 'quikbase', kind: 'key', keys: ['KeyS', 'ArrowDown'], key: 'S', text: 'Press S on your keyboard to walk back'},
  {id: 'walk_d', place: 'quikbase', kind: 'key', keys: ['KeyD', 'ArrowRight'], key: 'D', text: 'Press D on your keyboard to walk right'},
  {id: 'jump', place: 'quikbase', kind: 'key', keys: ['Space'], key: 'Space', text: 'Press Space on your keyboard to jump'},
  // owner round 13.10: never stuck here - the step moves on by itself after LOOK_GIVE_UP_MS (no coins without a drag)
  {id: 'look', place: 'quikbase', kind: 'drag', skipOn: 'look:timeout', skipTo: 'fly_on', text: 'Hold the left mouse button and drag to look around'},
  {id: 'fly_on', place: 'quikbase', kind: 'click', target: TARGET.tileFly, text: 'Click FLY to take off'},
  {id: 'fly_up', place: 'quikbase', kind: 'click', target: TARGET.flyUp, text: 'Click UP to fly higher'},
  {id: 'fly_down', place: 'quikbase', kind: 'click', target: TARGET.flyDown, text: 'Click DOWN to fly lower'},
  {id: 'fly_off', place: 'quikbase', kind: 'click', target: TARGET.tileFly, text: 'Click FLY again to land'},
  {id: 'controls_badge', place: 'quikbase', kind: 'badge', step: 'controls'},
  {id: 'store_open', place: 'quikbase', kind: 'click', target: TARGET.collectionStore, text: 'Your QuikCoins buy treasures. Open the Collection Store'},
  {id: 'store_close', place: 'quikbase', kind: 'click', target: TARGET.storeClose, advanceOn: 'store:closed', needs: 'store', without: 'go_portal', text: 'Close the store when you are ready'},
  {id: 'go_portal', place: 'quikbase', kind: 'wait', advanceOn: 'world:course', text: 'Walk into the glowing portal to start your course'},

  {id: 'open_menu', place: 'world', kind: 'click', target: TARGET.lessonsButton, advanceOn: 'menu:opened', text: 'Click All Lessons and Quizzes'},
  {id: 'start_tour_lesson', place: 'world', kind: 'click', target: '{lesson:start}', advanceOn: 'slide:started', back: 'open_menu', needs: 'menu', without: 'open_menu', text: 'Click Start Lesson'},
  {id: 'tour_slide', place: 'world', kind: 'panel', panel: 'slide', advanceOn: 'slide:done'},
  {id: 'slide_badge', place: 'world', kind: 'badge', step: 'slide'},
  {id: 'tutor_open', place: 'world', kind: 'click', target: TARGET.tileTutor, advanceOn: 'tutor:started', skipOn: 'tutor:refused', skipTo: 'quiz_menu', text: 'Click TUTOR to talk with {tutor}'},
  {id: 'tutor_answer', place: 'world', kind: 'wait', advanceOn: 'tutor:answered', suggestions: true, needs: 'tutor', without: 'tutor_open', text: 'Ask {tutor} a question'},
  {id: 'tutor_close', place: 'world', kind: 'click', target: TARGET.tileTutor, advanceOn: 'tutor:stopped', needs: 'tutor', without: 'quiz_menu', text: 'Great question! Click TUTOR to finish talking'},
  {id: 'quiz_menu', place: 'world', kind: 'click', target: TARGET.lessonsButton, advanceOn: 'menu:opened', text: 'Now a quick quiz. Click All Lessons and Quizzes'},
  {id: 'start_tour_quiz', place: 'world', kind: 'click', target: '{lesson:quiz}', advanceOn: 'quiz:started', back: 'quiz_menu', needs: 'menu', without: 'quiz_menu', text: 'Click Start Quiz'},
  {id: 'tour_quiz', place: 'world', kind: 'panel', panel: 'quiz', advanceOn: 'quiz:done'},
  {id: 'quiz_badge', place: 'world', kind: 'badge', step: 'quiz'},
  {id: 'real_menu', place: 'world', kind: 'click', target: TARGET.lessonsButton, advanceOn: 'menu:opened', skipOn: 'lesson:completed', skipTo: 'lesson_tutor', text: 'Ready for your first full lesson? Click All Lessons and Quizzes'},
  {id: 'start_real_lesson', place: 'world', kind: 'click', target: '{lesson:start}', advanceOn: 'lesson:started', back: 'real_menu', needs: 'menu', without: 'real_menu', skipOn: 'lesson:completed', skipTo: 'lesson_tutor', text: 'Click Start Lesson'},
  {id: 'real_lesson', place: 'world', kind: 'wait', advanceOn: 'lesson:completed', needs: 'lesson', without: 'real_menu'},
  // The lesson ends with its own tutor question: wait for an answer or TUTOR before Upgrade (Sol tour-lesson-end C);
  // no tutor open (or the minutes ran out) goes straight on.
  {id: 'lesson_tutor', place: 'world', kind: 'wait', advanceOn: 'tutor:stopped', skipOn: 'tutor:answered', skipTo: 'upgrade_open', needs: 'tutor', without: 'upgrade_open', recoverMs: 8000, text: "Answer {tutor}'s question, or click TUTOR to finish"},
  {id: 'upgrade_open', place: 'world', kind: 'click', target: TARGET.upgrade, advanceOn: 'plans:opened', skipOn: 'upgrade:unavailable', skipTo: 'leave', text: 'Well done! Click Upgrade to see everything you can learn'},
  {id: 'upgrade_close', place: 'world', kind: 'click', target: TARGET.plansClose, advanceOn: 'plans:closed', needs: 'plans', without: 'leave', text: 'Close this for now'},
  {id: 'leave', place: 'world', kind: 'click', target: TARGET.exit, advanceOn: 'leave:opened', text: 'Click Exit to go back Home', praisedText: 'Well done! Click Exit to go back Home'},
  {id: 'done'},
];

const byId = Object.fromEntries(STEPS_V1.map(step => [step.id, step]));
const same = (id, change = {}) => ({...byId[id], ...change});

/**
 * The simplified first visit in the owner's designed order (prototype rounds 4-12 + his 2026-10-08 real-build
 * message): the child's name and grade (portal) -> that grade's tour course on Unit 1: slide, badge, the Collection
 * Store, the tutor, the quiz -> "You did it! Explore more worlds" -> "What do you want to learn first?" (portal) -> Home,
 * where the learner taps that subject's card -> Quikbase at that course's portal: the controls, 10 coins each, Ready to
 * Explore - and the guided visit is over (owner 13 Oct; Sol owner-r25 Q3, owner-r25-review C).
 */
export const STEPS_V2 = [
  same('open_menu'),
  same('start_tour_lesson'),
  same('tour_slide'),
  same('slide_badge'),
  // owner 10-08: "as soon as user gets first badge ... guide user they can open reward store and then close it too"
  same('store_open', {place: 'world'}),
  same('store_close', {place: 'world', without: 'tutor_open'}),
  same('tutor_open'),
  same('tutor_answer'),
  same('tutor_close'),
  same('quiz_menu'),
  same('start_tour_quiz'),
  same('tour_quiz'),
  same('quiz_badge'),
  // "You did it!": the three badges and their coins, then Explore more worlds -> the portal's subject choice
  {id: 'tour_done', place: 'world', kind: 'panel', panel: 'done', advanceOn: 'quikbase:arrived'},
  same('walk_w'),
  same('walk_a'),
  same('walk_s'),
  same('walk_d', {pays: 'walk'}),
  same('jump', {pays: 'jump'}),
  same('look', {pays: 'look'}),
  same('fly_on'),
  same('fly_up'),
  same('fly_down'),
  same('fly_off', {pays: 'fly'}),
  // The guided visit ends with the controls badge (owner 13 Oct: "once they are inside the metaverse, then we do not need
  // to show them tour once again"; Sol owner-r25-review C "End onboarding after the controls badge"): no portal walk,
  // first full lesson, Upgrade or Leave steps after it.
  same('controls_badge'),
  {id: 'done'},
];

/** Which order runs: the designed one where the rollout switch is on (DEV), today's elsewhere (Sol Q3 B). */
export const JOURNEY_VERSION = SIMPLIFIED_FIRST_VISIT ? 2 : 1;
export const STEPS = JOURNEY_VERSION === 2 ? STEPS_V2 : STEPS_V1;

const INDEX = Object.fromEntries(STEPS.map((step, index) => [step.id, index]));

export const stepById = id => STEPS[INDEX[id]] || null;

/** The step's target test id, with the tour lesson filled in. */
export function targetOf(step, lessonId) {
  if (!step?.target) return '';
  if (step.target === '{lesson:start}') return lessonId ? startLessonTarget(lessonId) : '';
  if (step.target === '{lesson:quiz}') return lessonId ? startQuizTarget(lessonId) : '';
  return step.target;
}

/** True when `event` completes `step`. */
export function completes(step, event, lessonId) {
  if (!step || !event) return false;
  if (step.advanceOn) return event === step.advanceOn;
  switch (step.kind) {
    case 'key':
      return step.keys.some(code => event === `key:${code}`);
    case 'click':
      return event === `click:${targetOf(step, lessonId)}`;
    case 'drag':
      return event === 'drag';
    case 'badge':
      return event === 'badge:reported';
    default:
      return false;
  }
}

/** The step after `stepId` when `event` completes it; otherwise `stepId` unchanged. */
export function advance(stepId, event, lessonId) {
  const step = stepById(stepId);
  // The lesson menu closed without starting anything: ask for it again. A click on the menu button does not count as
  // opening it (the button waits for the course content), so the menu steps advance on 'menu:opened' (DEV 2026-10-10).
  if (step && step.back && event === 'menu:closed') return step.back;
  if (step && step.skipTo && event === step.skipOn) return step.skipTo;
  if (!step || step.id === 'done' || !completes(step, event, lessonId)) return stepId;
  return STEPS[INDEX[stepId] + 1].id;
}

/** How long a step's needed state may be missing before the tour moves to where the learner really is. */
export const RECOVER_MS = 1500;

/**
 * How long `stepId`'s needed state may be missing before the tour moves on. The lesson's closing tutor question
 * starts its session about 2 s after the lesson ends (DEV 2026-10-10), so that step waits longer.
 */
export const recoverDelay = stepId => stepById(stepId)?.recoverMs || RECOVER_MS;

/** The step to show when `stepId` needs a world state that `ui` says is gone; otherwise `stepId` unchanged. */
export function recoverStep(stepId, ui = {}) {
  const step = stepById(stepId);
  return step && step.needs && !ui[step.needs] ? step.without : stepId;
}

/**
 * The learner is in the other place from the next step (left the course world mid-visit, or opened a course before
 * the Quikbase steps): point them back instead of showing nothing (Sol tour-dead-ends B, dead end 2).
 */
export function elsewhereGuide(stepId, place) {
  const step = stepById(stepId);
  if (!step || !step.place || !place || step.place === place) return null;
  // The course-world tour is over once 'You did it!' is behind the learner: no card or hand in a course world for the
  // Quikbase part - it starts when the learner reaches Quikbase (owner 12 Oct #11; Sol owner-r24-dev-check Q2 B).
  if (JOURNEY_VERSION === 2 && place === 'world' && STEPS.findIndex(entry => entry.id === stepId) > STEPS.findIndex(entry => entry.id === 'tour_done')) {
    return null;
  }
  return place === 'quikbase'
    ? {text: 'Walk into the glowing portal to continue your course', target: ''}
    : {text: 'Your tour is in Quikbase. Click Exit to go there', target: TARGET.exit};
}

/** "6th Grade Science" for a course portal {grade, subject} (grade K is Kindergarten); '' when not known. */
export function portalLabel(portal) {
  const grade = String(portal?.grade ?? '').trim();
  const subject = String(portal?.subject ?? '').trim();
  if (!grade || !subject) return '';
  const name = subject.charAt(0).toUpperCase() + subject.slice(1).toLowerCase();
  if (/^k$/i.test(grade)) return `Kindergarten ${name}`;
  const n = Number(grade);
  if (!Number.isInteger(n) || n < 1) return '';
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : {1: 'st', 2: 'nd', 3: 'rd'}[n % 10] || 'th';
  return `${n}${suffix} Grade ${name}`;
}

/** Whether two portals name the same course: the world reports {grade: '7', subject: 'science'}, the token 7/Science. */
export function samePortal(a, b) {
  const key = p => `${String(p?.grade ?? '').trim().toLowerCase()}|${String(p?.subject ?? '').trim().toLowerCase()}`;
  return Boolean(a && b) && key(a) === key(b);
}

/** How long the look step waits for a drag before moving on by itself (owner round 13.10). */
export const LOOK_GIVE_UP_MS = 5000;

/** How long the Upgrade step waits for the learner's plan before treating it as not Free. */
export const PLAN_WAIT_MS = 10000;

/**
 * The HUD Upgrade button exists only on a Free plan (measured on DEV 2026-10-10: a paid learner's tour stuck on
 * "Click Upgrade"). 'show' once the plan says Free; 'skip' when it says otherwise, or has not loaded within
 * PLAN_WAIT_MS (a failed load stays null); otherwise 'wait' (Sol tour-upgrade-step B).
 */
export function upgradeStepFor(plan, waitedMs) {
  if (plan) return plan.isFree ? 'show' : 'skip';
  return waitedMs >= PLAN_WAIT_MS ? 'skip' : 'wait';
}

/**
 * Where a learner starts when this browser holds no progress: the first step whose badge the server does not
 * hold yet (Sol realbuild1 R3 - earned badges are the durable record). Steps after the quiz are guidance only,
 * so a learner who has every badge is done.
 */
export function initialStep(serverSteps = {}, place = 'quikbase') {
  if (JOURNEY_VERSION === 2) {
    // The designed order: the world tour first, then the controls in Quikbase; then the visit is over.
    if (!serverSteps.slide) return 'open_menu';
    if (!serverSteps.tutor) return 'tutor_open';
    if (!serverSteps.quiz) return 'quiz_menu';
    if (!serverSteps.controls) return place === 'world' ? 'tour_done' : 'walk_w';
    return 'done';
  }
  if (!serverSteps.controls) return 'walk_w';
  if (!serverSteps.slide) return place === 'world' ? 'open_menu' : 'go_portal';
  if (!serverSteps.tutor) return 'tutor_open';
  if (!serverSteps.quiz) return 'quiz_menu';
  return 'done';
}

/**
 * Coming back to a saved step: a panel the learner has already been shown is not shown again (owner 12 Oct #11; Sol
 * owner-r24-tour-again C) - "You did it!" moves on to the next step, and a badge moment whose badge is already held
 * moves past it. Every other step resumes where it was (a step left part-way was never fully shown).
 */
export function resumeStep(stepId, serverSteps = {}) {
  let id = stepId;
  for (let guard = 0; guard < STEPS.length && id; guard += 1) {
    const step = STEPS.find(entry => entry.id === id);
    const seen = id === 'tour_done' || (step && step.kind === 'badge' && serverSteps[step.step]);
    if (!seen) return id;
    const next = STEPS[STEPS.findIndex(entry => entry.id === id) + 1];
    id = next ? next.id : 'done';
  }
  return id;
}

/**
 * Arriving in a place: the portal walk is complete once the course world loads (the teleport reloads the page).
 */
export function onArrive(stepId, place) {
  if (stepId === 'go_portal' && place === 'world') return advance(stepId, 'world:course');
  // "Explore more worlds" -> the subject choice -> Quikbase: the controls tour starts there.
  if (stepId === 'tour_done' && place === 'quikbase') return advance(stepId, 'quikbase:arrived');
  return stepId;
}
